import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'

export interface ReportFilters {
  type?: 'daily' | 'weekly'
  period?: 'today' | 'week' | 'month' | 'all'
  search?: string
  patientId?: string
}

interface DailyReport {
  id: string
  content: string
  created_at: string
  report_type: string
  patient_id: string
  patient: {
    id: string
    full_name: string
  }
  sentiment_score?: number | null
}

interface WeeklySummary {
  id: string
  summary_text: string
  week_start_date: string
  week_end_date: string
  generation_type: string
  patient_id: string
  patient: {
    id: string
    full_name: string
  }
}

function getDateRange(period: string): { start: Date; end: Date } {
  const end = new Date()
  const start = new Date()

  switch (period) {
    case 'today':
      start.setHours(0, 0, 0, 0)
      break
    case 'week':
      start.setDate(start.getDate() - 7)
      break
    case 'month':
      start.setMonth(start.getMonth() - 1)
      break
    default:
      start.setFullYear(2020)
  }

  return { start, end }
}

async function fetchDailyReports(
  organizationId: string,
  filters: ReportFilters,
): Promise<DailyReport[]> {
  let query = supabase
    .from('daily_reports')
    .select(`
      id,
      content,
      created_at,
      report_type,
      sentiment_score,
      patient_id,
      patients!inner (
        id,
        full_name,
        organization_id,
        status
      )
    `)
    .eq('patients.organization_id', organizationId)
    .neq('patients.status', 'deleted')
    .order('created_at', { ascending: false })
    .limit(50)

  if (filters.period && filters.period !== 'all') {
    const { start } = getDateRange(filters.period)
    query = query.gte('created_at', start.toISOString())
  }

  if (filters.patientId) {
    query = query.eq('patient_id', filters.patientId)
  }

  if (filters.search) {
    query = query.ilike('content', `%${filters.search}%`)
  }

  const { data, error } = await query

  if (error) throw error

  return (data ?? []).map((report: any) => {
    const patient = report.patients as { id: string; full_name: string }
    return {
      id: report.id,
      content: report.content,
      created_at: report.created_at,
      report_type: report.report_type,
      patient_id: report.patient_id,
      patient: {
        id: patient.id,
        full_name: patient.full_name,
      },
      sentiment_score: report.sentiment_score,
    }
  })
}

async function fetchWeeklySummaries(
  organizationId: string,
  filters: ReportFilters,
): Promise<WeeklySummary[]> {
  let query = supabase
    .from('weekly_summaries')
    .select(`
      id,
      summary_text,
      week_start_date,
      week_end_date,
      generation_type,
      patient_id,
      patients!inner (
        id,
        full_name,
        organization_id,
        status
      )
    `)
    .eq('patients.organization_id', organizationId)
    .neq('patients.status', 'deleted')
    .order('week_end_date', { ascending: false })
    .limit(20)

  if (filters.period && filters.period !== 'all') {
    const { start } = getDateRange(filters.period)
    query = query.gte('week_end_date', start.toISOString())
  }

  if (filters.patientId) {
    query = query.eq('patient_id', filters.patientId)
  }

  const { data, error } = await query

  if (error) throw error

  return (data ?? []).map((summary: any) => {
    const patient = summary.patients as { id: string; full_name: string }
    return {
      id: summary.id,
      summary_text: summary.summary_text,
      week_start_date: summary.week_start_date,
      week_end_date: summary.week_end_date,
      generation_type: summary.generation_type,
      patient_id: summary.patient_id,
      patient: {
        id: patient.id,
        full_name: patient.full_name,
      },
    }
  })
}

export function useDailyReports(filters: ReportFilters = {}) {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['daily-reports', organizationId, filters],
    queryFn: () => fetchDailyReports(organizationId!, filters),
    enabled: !!organizationId,
  })
}

export function useWeeklySummaries(filters: ReportFilters = {}) {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['weekly-summaries', organizationId, filters],
    queryFn: () => fetchWeeklySummaries(organizationId!, filters),
    enabled: !!organizationId,
  })
}
